import React, { useState, useEffect, useCallback } from 'react';
import { 
  doc, 
  updateDoc, 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  deleteDoc,
  getDocs 
} from 'firebase/firestore';
import { db } from '../../firebase';
import { FaTimes, FaUser, FaTrash, FaBuilding, FaCalendar } from 'react-icons/fa';
import '../styles/projectWindow.css';
import ProjectTaskTable from './projectTaskTable';

const STATUS_TYPES = {
  todo: {
    label: 'To do',
    color: '#6b7280',
    description: 'Projekt der skal startes'
  },
  doing: {
    label: 'Doing',
    color: '#2563eb',
    description: 'Projekt under udførelse'
  },
  pause: {
    label: 'Pause',
    color: '#9333ea',
    description: 'Projekt sat på pause'
  },
  done: {
    label: 'Done',
    color: '#22c55e',
    description: 'Afsluttet projekt'
  }
};

function ProjectWindow({ project, onClose }) {
  const [name, setName] = useState(project.name);
  const [status, setStatus] = useState(project.status);
  const [description, setDescription] = useState(project.description || '');
  const [responsible, setResponsible] = useState(project.responsible || '');
  const [deadline, setDeadline] = useState(project.deadline || '');
  const [users, setUsers] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [linkedCustomer, setLinkedCustomer] = useState(null);

  // Debounce funktion til at håndtere auto-save
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Auto-save funktion
  const saveChanges = useCallback(async (newData) => {
    setIsSaving(true);
    try {
      const projectRef = doc(db, 'projects', project.id);
      await updateDoc(projectRef, {
        ...newData,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Fejl ved auto-save:', error);
    } finally {
      setIsSaving(false);
    }
  }, [project.id]);

  // Opret debounced version af saveChanges
  const debouncedSave = useCallback(
    debounce((data) => saveChanges(data), 1000),
    [saveChanges]
  );

  // Håndter ændringer i navn
  const handleNameChange = (e) => {
    const newName = e.target.value;
    setName(newName);
    debouncedSave({ name: newName });
  };

  // Håndter ændringer i status
  const handleStatusChange = (newStatus) => {
    setStatus(newStatus);
    saveChanges({ status: newStatus }); // Status gemmes med det samme uden debounce
  };

  // Tilføj denne nye handler
  const handleDescriptionChange = (e) => {
    const newDescription = e.target.value;
    setDescription(newDescription);
    debouncedSave({ description: newDescription });
  };

  const handleResponsibleChange = (e) => {
    const newResponsible = e.target.value;
    setResponsible(newResponsible);
    debouncedSave({ responsible: newResponsible });
  };

  const handleDeadlineChange = (newDeadline) => {
    setDeadline(newDeadline);
    debouncedSave({ deadline: newDeadline || null });
  };

  const handleOverlayClick = (e) => {
    // Tjek om klikket er på et projekt
    const isProjectClick = e.target.closest('.project-placeholder');
    if (!isProjectClick) {
      onClose();
    }
  };

  useEffect(() => {
    const q = query(
      collection(db, 'tasks'),
      where('projectId', '==', project.id)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const tasksData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      tasksData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setTasks(tasksData);
    });

    return () => unsubscribe();
  }, [project.id]);

  useEffect(() => {
    const q = query(collection(db, 'users'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const usersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersData);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!project.customerId) return;

    const unsubscribe = onSnapshot(doc(db, 'customers', project.customerId), (doc) => {
      if (doc.exists()) {
        setLinkedCustomer({ id: doc.id, ...doc.data() });
      }
    });

    return () => unsubscribe();
  }, [project.customerId]);

  const handleDeleteProject = async () => {
    if (window.confirm('Er du sikker på, at du vil slette dette projekt? Alle opgaver i projektet vil også blive slettet.')) {
      try {
        // Slet alle projektets opgaver
        const tasksQuery = query(collection(db, 'tasks'), where('projectId', '==', project.id));
        const taskSnapshot = await getDocs(tasksQuery);
        const deleteTaskPromises = taskSnapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(deleteTaskPromises);

        // Slet selve projektet
        await deleteDoc(doc(db, 'projects', project.id));
        onClose();
      } catch (error) {
        console.error('Fejl ved sletning af projekt:', error);
      }
    }
  };

  const getDeadlineStatus = (deadline) => {
    if (!deadline) return null;
    
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const daysUntilDeadline = Math.ceil((deadlineDate - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilDeadline < 0) {
      return 'overdue';
    } else if (daysUntilDeadline <= 3) {
      return 'warning';
    }
    return null;
  };

  const deadlineStatus = getDeadlineStatus(deadline);

  return (
    <>
      <div className="project-window-overlay" onClick={handleOverlayClick}></div>
      <div className="project-window-wrapper">
        <div className="project-window">
          <button className="close-window" onClick={onClose}>
            <FaTimes />
          </button>

          {linkedCustomer && (
            <div className="window-section customer-info-section">
              <div className="section-header">
                <h2>Tilknyttet kunde</h2>
                <div className="section-description">
                  Dette projekt er tilknyttet en kunde i CRM
                </div>
              </div>
              <div className="customer-details">
                <div className="customer-name">
                  <FaBuilding />
                  <span>{linkedCustomer.name}</span>
                </div>
                {linkedCustomer.contactPerson && (
                  <div className="customer-contact">
                    <FaUser />
                    <span>{linkedCustomer.contactPerson}</span>
                  </div>
                )}
                <div className="customer-value">
                  Værdi: {new Intl.NumberFormat('da-DK', {
                    style: 'currency',
                    currency: 'DKK',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                  }).format(linkedCustomer.value || 0)}
                </div>
              </div>
            </div>
          )}

          <div className="window-section">
            <div className="section-header">
              <h2>Projekt detaljer</h2>
              <div className="section-description">
                Grundlæggende information om projektet
                {isSaving && <span className="saving-indicator">Gemmer...</span>}
              </div>
            </div>

            <div className="form-group">
              <label>Projekt navn</label>
              <input
                type="text"
                value={name}
                onChange={handleNameChange}
                placeholder="Indtast projekt navn"
                required
              />
            </div>

            <div className="form-group">
              <label>
                <FaCalendar className="input-icon" />
                Deadline
                {deadlineStatus === 'warning' && (
                  <span className="deadline-warning-text">Deadline nærmer sig</span>
                )}
                {deadlineStatus === 'overdue' && (
                  <span className="deadline-overdue-text">Deadline overskredet</span>
                )}
              </label>
              <input
                type="date"
                className={`deadline-input ${deadlineStatus ? `deadline-${deadlineStatus}` : ''}`}
                value={deadline}
                onChange={(e) => handleDeadlineChange(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Beskrivelse</label>
              <textarea
                value={description}
                onChange={handleDescriptionChange}
                placeholder="Tilføj en beskrivelse af projektet..."
                rows={4}
              />
            </div>

            <div className="form-group">
              <label>Status</label>
              <div className="status-options">
                {Object.entries(STATUS_TYPES).map(([key, { label, color, description }]) => (
                  <div
                    key={key}
                    className={`status-option ${status === key ? 'active' : ''}`}
                    onClick={() => handleStatusChange(key)}
                    style={{ '--status-color': color }}
                  >
                    <div className="status-header">
                      <div className="status-dot"></div>
                      <span className="status-label">{label}</span>
                    </div>
                    <p className="status-description">{description}</p>
                  </div>
                ))}
              </div>
            </div>

            <ProjectTaskTable project={project} tasks={tasks} />

            <div className="delete-section">
              <div className="delete-section-content">
                <div>
                  <h3>Slet projekt</h3>
                  <p>Denne handling kan ikke fortrydes. Alle projektets opgaver vil også blive slettet.</p>
                </div>
                <button className="delete-button" onClick={handleDeleteProject}>
                  <FaTrash />
                  <span>Slet projekt</span>
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}

export default ProjectWindow;
